import React, { useEffect, useState } from "react";
import { Image, Stack } from "react-bootstrap";
import { TransactionHistory } from "@nestcoinco/hyper-api-gateway-api-client";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import "./Wallet.scss";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import PlusIcon from "../../components/icons/PlusIcon";
import ArrowTopRightIcon from "../../components/icons/ArrowTopRightIcon";
import HyperIcon from "../../components/icons/HyperIcon";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../hooks/useDisclosure";
import NotificationButton from "../../components/Buttons/NotificationButton/NotificationButton";
import UserAvatar from "../../components/UserAvatar/UserAvatar";
import TransactionsEmptyState from "../../components/wallet/TransactionsEmptyState/TransactionsEmptyState";
import TransactionItem from "../../components/wallet/TransactionItem/TransactionItem";
import ImageStripes from "../../images/stripes.png";
import { handleGetMyTransactionHistory } from "../../api-clients/walletsTransactionsClient";
import useWalletBalances from "../../hooks/useWalletBalances";
import { formatHprBalance } from "../../utils/helpers";
import useTopupFlow from "../../hooks/useTopupFlow";
import TopupFlow from "../../components/topup/TopupFlow/TopupFlow";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import { usePlayGameMetaDescription } from "../../utils/constants";
import Skeleton from "../../components/Skeleton/Skeleton";
import SkeletonList from "../../components/SkeletonList/SkeletonList";
import {
  initiateTopupTriggerAtom,
  InitiateTopupTriggers,
} from "../../jotai/atoms/analytics";
import useHprPurchasePlansQuery from "../../react-query/queries/use-hpr-purchase-plans";
import PrimaryButtonWithBonusTag from "../../components/onboarding/PrimaryButtonWithBonusTag/PrimaryButtonWithBonusTag";
import useHprFiatFormatter from "../../hooks/useHprFiatFormatter";

export default function Wallet() {
  const {
    modalsProps,
    ipLocationCountry,
    handleTopup,
    handleGetDeviceLocationInfo,
  } = useTopupFlow();
  const [transactions, setTransactions] = useState<TransactionHistory[]>();
  const setInitiateTopupTrigger = useSetAtom(initiateTopupTriggerAtom);
  const walletBalances = useWalletBalances();
  const hprBalance = walletBalances?.HPR.availableBalance;
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation("wallet");
  const { hasWelcomeBonus } = useHprPurchasePlansQuery();
   const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
   const displayValue = formatHprToFiatValue(hprBalance);


  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const goToTopup = () => {
    setInitiateTopupTrigger(InitiateTopupTriggers.WALLET_TOPUP);
    handleTopup();
  };

  const handleCashout = () => {
    openDownloadAppPrompt();
  };

  useEffect(() => {
    (async () => {
      const transactionsList = await handleGetMyTransactionHistory();
      setTransactions(transactionsList);
    })();
  }, []);

  return (
    <>
      {renderHeaders({
        title: t("metadata:hyper_wallet_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:hyper_wallet_hyper_gaming_app"),
        ),
      })}

      <div className="px-3 py-4 WalletPage">
        <Stack gap={4}>
          <header className="d-flex justify-content-between align-items-center PageHeader">
            <div className="d-flex align-items-center AvatarWithTitle">
              <UserAvatar />
              <h1 className="fs-6 Hanson-700 SpecialFontSettings PageTitle">
                {t("wallet")}
              </h1>
            </div>
            <NotificationButton hasNotifications />
          </header>

          <div className="BalanceCard">
            <div
              className={`px-3 d-flex justify-content-between WalletBalance ${isFiatDisplay ? "FiatMode" : ""}`}
            >
              <Stack
                gap={3}
                className={`align-items-start ${isFiatDisplay ? "w-100 justify-content-center" : ""}`}
              >
                <Skeleton
                  width={92}
                  height={24}
                  isLoading={hprBalance === undefined}
                  style={{ background: "#FFFFFF4D" }}
                  shimmerStyle={{ background: "#0000000A" }}
                >
                  {!isFiatDisplay && hprBalance !== undefined && (
                    <Stack
                      direction="horizontal"
                      gap={1}
                      className="align-items-end Grotesk SpecialFontSettings HprBalance"
                    >
                      <span className="HprAmount">
                        {formatHprBalance(hprBalance)}
                      </span>
                      <span className="Currency">HPR</span>
                    </Stack>
                  )}
                  {isFiatDisplay && hprBalance !== undefined && (
                    <Stack
                      direction="horizontal"
                      gap={1}
                      className="FiatBalance align-items-center"
                    >
                      <span className="FiatCurrencySymbol Grotesk SpecialFontSettings">
                        {displayValue.charAt(0)}
                      </span>
                      <span className="FiatAmount Grotesk SpecialFontSettings">
                        {displayValue.slice(1)}
                      </span>
                    </Stack>
                  )}
                </Skeleton>
                {!isFiatDisplay && (
                  <Skeleton
                    width={40}
                    height={14}
                    isLoading={hprBalance === undefined}
                    style={{ background: "#FFFFFF4D" }}
                    shimmerStyle={{ background: "#0000000A" }}
                  >
                    <p className="Grotesk SpecialFontSettings FiatEquivalent">
                      {displayValue}
                    </p>
                  </Skeleton>
                )}
              </Stack>
              <HyperIcon className="z-1" />
              <Image src={ImageStripes} alt="" className="StripeImage" />
            </div>
            <Stack direction="horizontal" gap={3} className="p-3 Ctas">
              <Skeleton
                isLoading={hprBalance === undefined}
                width={90}
                height={40}
              >
                <PrimaryButtonWithBonusTag
                  text={t("top_up")}
                  buttonClassName="Topup"
                  textClassName="gap-1"
                  leftImage={<PlusIcon variant="thick" />}
                  onClick={goToTopup}
                  showBonusTag={hasWelcomeBonus}
                />
              </Skeleton>
              <Skeleton
                isLoading={hprBalance === undefined}
                width={105}
                height={40}
              >
                <PrimaryActionButton
                  text={t("cash_out")}
                  buttonClassName="Cashout"
                  textClassName="gap-1"
                  leftImage={<ArrowTopRightIcon />}
                  onClick={handleCashout}
                />
              </Skeleton>
            </Stack>
          </div>

          <Stack
            style={{
              gap: !transactions ? 12 : transactions.length === 0 ? 16 : 24,
            }}
            className="align-items-start TransactionHistory"
          >
            <h2 className="text-capitalize fs-6 fw-bold Grotesk SpecialFontSettings Heading">
              {t("transaction_history")}
            </h2>
            <SkeletonList
              noOfItems={6}
              skeletonProps={{ isLoading: !transactions, height: 40 }}
            />
            {transactions?.length === 0 && (
              <TransactionsEmptyState
                handleTopup={handleTopup}
                isFiat={isFiatDisplay}
              />
            )}
            {transactions && transactions.length > 0 && (
              <Stack gap={4}>
                {transactions.map((transaction) => (
                  <TransactionItem key={transaction.id} {...transaction} />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.CASHOUT}
      />
      <TopupFlow
        {...modalsProps}
        handleTopup={handleTopup}
        ipLocationCountry={ipLocationCountry}
        handleGetDeviceLocationInfo={handleGetDeviceLocationInfo}
      />
    </>
  );
}
